import { useEffect, useRef, useState } from 'react'
import './SegmentedControl.css'

/**
 * Page-level scope switch — `المفعلين` / `الموقوفين` / … .
 *
 * These decide *which list you are looking at*, which is a page concept, not a table
 * control, so they read as a scope strip under the page title rather than as
 * underline tabs buried in a card toolbar. `count` renders the size of a scope
 * when the page knows it.
 *
 * @param {{id: string, label: string, count?: number,
 *          icon?: import('react').ElementType}[]} items  a count is a SIZE, and
 *   every count in the row is drawn the same way. What a figure means belongs
 *   on the cards below, where there is room to say it — see the stylesheet.
 * @param {'pill'|'underline'} [variant]  `pill` is the compact track — a scope
 *   strip for sections rather than slices, and it draws `icon` too. `underline`
 *   is the quieter of the two: no track, no thumb, just a rule under the row and
 *   a mark under the chosen tab. It is for a switch that sits INSIDE a section
 *   that already has its own heading and controls, where a filled pill reads as
 *   a fourth control competing with them.
 *
 * A scope strip that also has to say how BIG each scope is is a StatStrip with
 * `activeId` / `onSelect`, not a variant of this — see the users list. Two
 * components drawing near-identical cards is how they drift apart.
 */
export default function SegmentedControl({
  items,
  value,
  onChange,
  ariaLabel,
  variant = 'pill',
}) {
  const underline = variant === 'underline'
  const trackRef = useRef(null)
  const [more, setMore] = useState({ start: false, end: false })

  /* WHAT DOES NOT FIT IS SAID TO BE THERE. The track scrolls sideways with its scrollbar hidden,
     so on a narrow screen the last tab was simply cut off, with nothing to say more existed
     (Taiga #1543). Each edge with tabs beyond it fades out — see the stylesheet. `scrollLeft`
     runs negative in a right-to-left track, hence the absolute value. */
  useEffect(() => {
    const track = trackRef.current
    if (!track) return undefined
    const measure = () => {
      const travelled = Math.abs(track.scrollLeft)
      const room = track.scrollWidth - track.clientWidth
      setMore({ start: travelled > 1, end: room - travelled > 1 })
    }
    measure()
    track.addEventListener('scroll', measure, { passive: true })
    const observer = new ResizeObserver(measure)
    observer.observe(track)
    return () => {
      track.removeEventListener('scroll', measure)
      observer.disconnect()
    }
  }, [items.length])

  // The chosen tab is never the one left out of sight.
  useEffect(() => {
    trackRef.current?.querySelector('.is-selected')?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [value])

  return (
    <div
      ref={trackRef}
      className={underline ? 'segtabs' : 'segmented'}
      role="tablist"
      aria-label={ariaLabel}
      data-more-start={more.start || undefined}
      data-more-end={more.end || undefined}
    >
      {items.map((item) => {
        const selected = item.id === value
        const base = underline ? 'segtabs' : 'segmented'
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={selected}
            className={`${base}__item${selected ? ' is-selected' : ''}`}
            onClick={() => onChange(item.id)}
          >
            {item.icon ? (
              <item.icon
                className={`${base}__icon`}
                size={15}
                strokeWidth={2}
                aria-hidden="true"
              />
            ) : null}
            <bdi>{item.label}</bdi>
            {item.count != null && (
              <span className={`${base}__count`}>{item.count.toLocaleString('en-US')}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}
